import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock, Sparkles, Check, UserPlus } from "lucide-react";
import { SummariesCounter } from "@/components/SummariesCounter";

/**
 * Blocking paywall shown to anonymous visitors after the 3rd free summary.
 * Design mirrors the Pro paywall for visual consistency, but swaps the Stripe
 * CTA for a "Create a free account → +2 free summaries" button. A secondary
 * CTA leads to /signup too (credit checkout requires authentication).
 */
export const AnonSignupModal = () => {
  const { t } = useTranslation();

  // We display raw French copy as fallback — the next-session i18n keys will
  // slot in but the UX is already shippable without them.
  const marker = t("anon.limit_marker", "LIMITE ATTEINTE · ESSAI SANS COMPTE");
  const title = t("anon.limit_title", "Vous avez utilisé votre résumé d'essai.");
  const body = t(
    "anon.limit_body",
    "Créez un compte gratuit en 15 secondes pour débloquer 2 résumés supplémentaires. Ensuite, des packs dès 2 € les 20 résumés — pas d'abonnement, pas de carte demandée pour l'essai."
  );
  const perks = [
    t("anon.perk_1", "Achetez des crédits dès 2 € les 20 résumés"),
    t("anon.perk_2", "Historique persistant de vos résumés"),
    t("anon.perk_3", "Avec les crédits : 3 IA au choix, questions de suivi et actions en 1 clic"),
    t("anon.perk_4", "Export PDF, partage LinkedIn, mode enseignant"),
  ];

  return (
    <div
      data-testid="anon-signup-paywall"
      className="border-t border-border bg-secondary/40 min-h-[70vh] flex items-center justify-center px-4 py-6 md:px-6 md:py-16"
    >
      <div className="max-w-2xl w-full bg-card border border-border p-5 md:p-10 lg:p-14">
        <div className="flex items-center gap-3 mb-3 md:mb-6">
          <Lock className="w-5 h-5 text-[#002FA7]" />
          <div className="sux-marker">{marker}</div>
        </div>
        <h1
          className="text-2xl md:text-4xl lg:text-5xl font-black tracking-tight mb-3 md:mb-4 leading-[1.1]"
          style={{ fontFamily: "Outfit" }}
        >
          {title}
        </h1>
        <p className="text-muted-foreground mb-5 md:mb-10 text-[13px] md:text-[15px] leading-relaxed">
          {body}
        </p>

        <div className="border border-[#002FA7] p-4 md:p-6 lg:p-8 mb-5 md:mb-8 bg-card">
          <div className="flex items-baseline justify-between mb-3 md:mb-4 flex-wrap gap-2">
            <h2
              className="text-xl md:text-2xl font-black tracking-tight"
              style={{ fontFamily: "Outfit" }}
            >
              {t("anon.free_account_title", "Compte gratuit")}
            </h2>
            <div className="text-right">
              <div
                className="text-4xl md:text-5xl font-black text-[#002FA7] leading-none"
                style={{ fontFamily: "Outfit" }}
              >
                2 €
              </div>
              <div className="sux-marker mt-1">
                {t("anon.free_account_marker", "LES 20 RÉSUMÉS")}
              </div>
            </div>
          </div>

          <ul className="space-y-2 md:space-y-2.5 text-[13px] md:text-sm">
            {perks.map((line) => (
              <li key={line} className="flex items-center gap-3">
                <Check className="w-4 h-4 text-[#002FA7] flex-shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <SummariesCounter compact testid="anon-summaries-counter" />
        <Link
          to="/signup"
          data-testid="anon-signup-cta"
          className="w-full bg-[#002FA7] text-white font-mono uppercase tracking-[0.2em] text-xs py-4 md:py-5 hover:bg-[#00227A] transition-colors flex items-center justify-center gap-3"
        >
          <UserPlus className="w-4 h-4" />
          {t("anon.cta_signup", "Créer mon compte gratuit — 15 secondes")}
        </Link>

        <Link
          to="/signup"
          data-testid="anon-signup-pro-cta"
          className="w-full mt-3 border border-border font-mono uppercase tracking-[0.2em] text-[11px] py-3 md:py-4 hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-colors flex items-center justify-center gap-2"
        >
          <Sparkles className="w-3 h-3" />
          {t(
            "anon.cta_pro",
            "Acheter des crédits dès 2 € (nécessite un compte)"
          )}
        </Link>

        <div className="mt-5 md:mt-6 flex items-center justify-center gap-3 text-[11px] font-mono uppercase tracking-[0.15em] text-muted-foreground">
          <span>{t("anon.already_account", "Vous avez déjà un compte ?")}</span>
          <Link
            to="/login"
            data-testid="anon-login-link"
            className="text-[#002FA7] hover:text-[#00227A] underline"
          >
            {t("anon.login", "Se connecter")}
          </Link>
        </div>
      </div>
    </div>
  );
};
